import type { MercyState, StackIndex } from "../types/game";
import { STACK_INDICES } from "../types/game";
import type { HouseRules } from "./houseRules";
import { starfallShuffle } from "./starfall";

/**
 * The Mercy Rule: a player who ends a turn too far behind the field bursts part
 * of the gap back onto random piles. A catch-up rule - one bad pile should not
 * decide a round.
 *
 * "Behind" is measured against the OTHER players, not against the player's own
 * deal: the trigger is a deck at least half a deal (`mercyGap`) bigger than the
 * average opponent still holding cards. A line drawn off the player's own deal
 * could not be reached at a small table - two and a half deals at two seats is
 * more cards than the whole pool - and in a one-on-one, falling behind barely
 * grows a deck at all: it is the GAP to the other player that opens. Measured
 * against the average opponent, the same distance means the same thing at two
 * seats and at eight. The deal still scales it, so a forty-card deal is not
 * tripped by the same five cards as a six-card one - up to a point: past
 * `MERCY_GAP_MAX` the line stops growing, or four decks between two players
 * would need a 52-card gap and a player holding 54 against 5 got nothing. Every
 * seat is dealt the same count, so the round carries one number
 * (`MercyState.dealt`).
 *
 * The burst is a third of the gap, rounded up: help that grows with the
 * deficit, and can never carry a player past the average (let alone into the
 * lead). After a burst the player rests for `MERCY_COOLDOWN_TURNS` of their
 * own turns before it can fire again (`PlayerState.mercyRest`), and once
 * anybody is out the rule stands down - a last-lap swing would decide a round
 * already decided.
 *
 * This file is the arithmetic. The burst itself is `resolveMercy` (moves.ts),
 * run from `endTurn` after Constellations and before the next player's locks
 * are chosen, so a burst changes the board those locks are picked against. Its
 * cards are played like comets - a wild fires, a card can light a star - and a
 * meter they fill fires its event in the same turn.
 *
 * Mirrors services/game-server/internal/engine/mercy.go. The two step the same
 * generator, but seeds differ between languages (as Starfall's do).
 */

/** Fewest seats for the rule to mean anything: a solo deck always holds most of the cards. */
export const MERCY_MIN_PLAYERS = 2;
/** The gap that bursts is the deal divided by this, rounded up: half a deal. */
export const MERCY_GAP_DIVISOR = 2;
/** The most the gap can ever be, however big the deal. */
export const MERCY_GAP_MAX = 20;
/** The burst is the gap divided by this, rounded up: a third of it. */
export const MERCY_BURST_DIVISOR = 3;
/** Own turns a player rests after a burst before Mercy can fire for them again. */
export const MERCY_COOLDOWN_TURNS = 3;

/**
 * Whether the Mercy Rule is in force at this table: the flag, plus an opponent.
 * The seat count is part of the rule for Streaks' reason (`streaksApply`): a
 * solo run has nobody to fall behind.
 */
export function mercyApplies(rules: HouseRules, playerCount: number): boolean {
  return rules.mercyRule && playerCount >= MERCY_MIN_PLAYERS;
}

/**
 * How far ahead of the average opponent a deck has to be to burst, in a round
 * that dealt each seat `dealt` cards: half the deal, rounded up, and never more
 * than `MERCY_GAP_MAX`. Dealt 6, a deck 3 cards bigger than the average
 * opponent bursts; dealt 7, 4; dealt 104, 20.
 */
export function mercyGap(dealt: number): number {
  return Math.min(Math.floor((dealt + MERCY_GAP_DIVISOR - 1) / MERCY_GAP_DIVISOR), MERCY_GAP_MAX);
}

/**
 * How many cards a deck of `deckSize` bursts, against `others` - the deck sizes
 * of every opponent still holding cards - in a round that dealt `dealt` a seat:
 * a third of how far it is ahead of their average, rounded up, once that is at
 * least `mercyGap` - else 0.
 *
 * Integer arithmetic throughout, the average never divided out (the gap is
 * compared and divided as `deck * n - sum`), so the Go engine reaches the same
 * answer by the same steps.
 *
 * A `dealt` of 0 never bursts: a round saved before the deal was recorded has
 * no deal to scale by. Nobody else holding cards never bursts either: there is
 * no field to be behind.
 *
 * Never the whole deck. The burst is a mercy, not a way out: a deck of one card
 * keeps it. (A wild in the burst can still take the last card with its
 * follow-up, as it would anywhere.)
 */
export function mercyBurstSize(deckSize: number, others: readonly number[], dealt: number): number {
  const n = others.length;
  if (dealt <= 0 || deckSize <= 1 || n === 0) return 0;
  const ahead = deckSize * n - others.reduce((a, b) => a + b, 0); // the gap, times n
  if (ahead < mercyGap(dealt) * n) return 0;
  const share = MERCY_BURST_DIVISOR * n;
  return Math.min(Math.floor((ahead + share - 1) / share), deckSize - 1);
}

/** A fresh generator for a round being dealt, which dealt each seat `dealt` cards. */
export function newMercyState(seed: number, dealt: number): MercyState {
  return { rng: seed >>> 0, dealt };
}

/**
 * The piles `count` burst cards land on, in landing order: laps of the five in
 * a fresh random order, so every pile takes one before any takes a second. A
 * burst is meant to spread the deck back over the table, and independent draws
 * would stack half of a small burst on one pile.
 */
export function mercyPiles(m: MercyState, count: number): StackIndex[] {
  const out: StackIndex[] = [];
  while (out.length < count) out.push(...starfallShuffle(m, STACK_INDICES));
  return out.slice(0, count);
}
